import React from 'react';

import { Alert, Checkbox, Input } from 'antd';
import Axios from 'axios';

import { getFileTypeIcons } from '@libs/NwUtils';

import { NwButton, NwCancelButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import { NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';
import SignatureSelector from '@components/Gui/NwForm/SignatureSelector';
import NwIcon from '@components/Gui/NwIcon';

import EmailToSendItem from './EmailToSendItem';
import {
    updateProjectDocumentSelection,
    updateModelDocumentSelection,
    updateModelSelection,
    addEmailToModel,
    removeEmailFromModel,
} from './emailUtils';
import { useSendProjectDrawer } from './SendProjectDrawerContext';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const { TextArea } = Input;

/**
 * EmailsFormWrapper component for managing email sending in the SendProjectDrawer
 * Contains email options, document selection, recipients management, and sending functionality
 */
const EmailsFormWrapper = ({ onClose }) => {
    const { state, actions } = useSendProjectDrawer();
    const { emailItems, subject, message, signatureID, acknowledgeEvents, isSending, errorSending, currentProject } =
        state;

    /**
     * Handles project document selection
     */
    const onSelectProjectDocument = (documentID, e) => {
        const newEmailItems = updateProjectDocumentSelection(emailItems, documentID, e.target.checked);
        actions.setEmailItems(newEmailItems);
    };

    /**
     * Handles model document selection
     */
    const onSelectModelDocument = (modelID, documentID, e) => {
        const newEmailItems = updateModelDocumentSelection(emailItems, modelID, documentID, e.target.checked);
        actions.setEmailItems(newEmailItems);
    };

    /**
     * Handles model selection for email
     */
    const onSelectModelForEmail = (ModelID, check) => {
        const newEmailItems = updateModelSelection(emailItems, ModelID, check);
        actions.setEmailItems(newEmailItems);
    };

    /**
     * Handles adding email to model
     */
    const onAddEmail = (ModelID, Email) => {
        const newEmailItems = addEmailToModel(emailItems, ModelID, Email);
        actions.setEmailItems(newEmailItems);
    };

    /**
     * Handles removing email from model
     */
    const onRemoveEmail = (ModelID, Email) => {
        const newEmailItems = removeEmailFromModel(emailItems, ModelID, Email);
        actions.setEmailItems(newEmailItems);
    };

    /**
     * Handles sending messages
     */
    const onSendMessages = async () => {
        if (emailItems && emailItems.Recipients.length > 0) {
            actions.setSendingState(true);
            const emailsToSend = [];

            for (const recipient of emailItems.Recipients) {
                if (recipient.Selected && recipient.Emails.length > 0) {
                    const modelDocuments = recipient.Attachments.filter((attachment) => attachment.Selected).map(
                        (attachment) => attachment.DocumentID
                    );
                    const projectDocuments = emailItems.ProjectDocuments.filter(
                        (attachment) => attachment.Selected
                    ).map((attachment) => attachment.DocumentID);
                    const allDocuments = [...modelDocuments, ...projectDocuments];

                    const data = {
                        DocVersionGUID: recipient.DocVersion.DocVersionGUID,
                        SendMethod: 'SendGrid',
                        Subject: subject,
                        AsAttachment: false,
                        Message: message,
                        DocumentsToAttach: [...allDocuments],
                        Recipients: recipient.Emails.map((email) => ({
                            ContactID: recipient.ModelData.ID,
                            To: email.email,
                            Ccs: email.ccs,
                        })),
                    };

                    if (signatureID) {
                        data.SignatureID = signatureID;
                    }

                    if (acknowledgeEvents) {
                        let eventsMap = {};
                        if (recipient.DocVersion.EventDatesModels && recipient.DocVersion.EventDatesModels.length > 0) {
                            eventsMap.CheckEvents = [...recipient.DocVersion.EventDatesModels];
                        }
                        if (recipient.DocVersion.Travels && recipient.DocVersion.Travels.length > 0) {
                            eventsMap.CheckTravels = [...recipient.DocVersion.Travels];
                        }
                        if (recipient.DocVersion.FollowUps && recipient.DocVersion.FollowUps.length > 0) {
                            eventsMap.CheckFollowUps = [...recipient.DocVersion.FollowUps];
                        }

                        const AcknowledgeOnSend = Object.keys(eventsMap).map((eventType) => {
                            return {
                                Action: eventType,
                                Data: { Events: eventsMap[eventType] },
                            };
                        });
                        if (AcknowledgeOnSend.length > 0) {
                            data.ActionsBefore = AcknowledgeOnSend;
                        }
                    }
                    emailsToSend.push(data);
                }
            }

            if (emailsToSend && emailsToSend.length > 0) {
                try {
                    await Axios.post('/docVersions/sendmessage', emailsToSend);
                    actions.setSendingState(false);
                    onClose();
                } catch (error) {
                    actions.setErrorSending('there was an error sending the message');
                    actions.setSendingState(false);
                }
            } else {
                actions.setErrorSending('At least a recipient e-mail address is needed to send the message');
                actions.setSendingState(false);
            }
        }
    };

    return (
        <div className="emails-form-wrapper">
            <NwDivider>E-mail options for all recipients</NwDivider>
            <div className="email-options">
                <div className="email-options-subject">
                    <small>e-mail subject</small>
                    <Input
                        placeholder="subject"
                        value={subject}
                        onChange={(e) => actions.setEmailFormData({ subject: e.target.value })}
                    />
                </div>
                <div className="email-options-message">
                    <small>message</small>
                    <TextArea
                        rows={4}
                        value={message}
                        onChange={(e) => actions.setEmailFormData({ message: e.target.value })}
                    />
                </div>
                <div className="email-options-signature">
                    <small>signature</small>
                    <SignatureSelector
                        value={signatureID}
                        onSelectSignature={(signature) => actions.setEmailFormData({ signature })}
                        onChange={(signatureID) => actions.setEmailFormData({ signatureID })}
                    />
                </div>
                <div className="email-options-acknowledgement">
                    <Checkbox
                        checked={acknowledgeEvents}
                        onChange={(e) => actions.setEmailFormData({ acknowledgeEvents: e.target.checked })}
                    >
                        set events as acknowledged
                    </Checkbox>
                </div>
            </div>

            {emailItems.ProjectDocuments.length > 0 && (
                <div className="project-general-documents">
                    <NwDivider>Project documents (selected will be attached to all e-mails sent)</NwDivider>
                    {emailItems.ProjectDocuments.map((doc) => (
                        <div className="project-general-document" key={doc.DocumentID}>
                            <Checkbox
                                checked={doc.Selected}
                                onChange={(e) => onSelectProjectDocument(doc.DocumentID, e)}
                            >
                                <NwIcon icon={getFileTypeIcons(doc.DocumentData.Extension)} />
                                {doc.DocumentData.Title}
                            </Checkbox>
                        </div>
                    ))}
                </div>
            )}

            <NwDivider>Recipients</NwDivider>
            <div className="emails-to-send">
                {emailItems.Recipients.map((recipient) => (
                    <EmailToSendItem
                        key={recipient.ModelData.ID}
                        message={message}
                        signature={state.signature}
                        onAddEmail={onAddEmail}
                        onRemoveEmail={onRemoveEmail}
                        onSelect={onSelectModelForEmail}
                        onSelectModelDocument={onSelectModelDocument}
                        recipient={recipient}
                    />
                ))}
            </div>

            {errorSending && (
                <Alert style={{ marginTop: '1rem' }} message="Error" description={errorSending} type="error" showIcon />
            )}

            <div className="buttons-bar">
                <NwFormButtonsBar
                    left={<NwCancelButton onClick={onClose} />}
                    right={
                        <NwButton
                            onClick={onSendMessages}
                            loading={isSending}
                            label="Send"
                            primary
                            icon={byPrefixAndName.fasl['paper-plane']}
                        />
                    }
                />
            </div>
        </div>
    );
};

export default EmailsFormWrapper;
